import { useState, useCallback, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import styles from './SplitsGlobaisPage.module.css'
import {
  fetchGlobalSplits,
  createGlobalSplit,
  updateGlobalSplit,
  deleteGlobalSplit,
  fetchAvatarMascotSplits,
  createAvatarMascotSplit,
  updateAvatarMascotSplit,
  deleteAvatarMascotSplit,
  fetchAvatarAdminSplits,
  createAvatarAdminSplit,
  updateAvatarAdminSplit,
  deleteAvatarAdminSplit,
} from '@/lib/splits'
import { SplitFormRows } from './SplitFormRows'
import {
  createEmptySplitFormItem,
  isPersistedSplitId,
  mapApiSplitToFormItem,
  toSplitCreatePayload,
  toSplitUpdatePayload,
  validateSplitFormItems,
  type SplitFormItem,
} from './split-form'

export function SplitsPadraoTab({
  category = 'global',
}: {
  category?: 'global' | 'avatar_mascot' | 'avatar_admin'
}) {
  const isAvatarAdmin = category === 'avatar_admin'
  const isAvatarMascot = category === 'avatar_mascot'
  const fetchSplits = isAvatarAdmin
    ? fetchAvatarAdminSplits
    : isAvatarMascot
      ? fetchAvatarMascotSplits
      : fetchGlobalSplits
  const createSplit = isAvatarAdmin
    ? createAvatarAdminSplit
    : isAvatarMascot
      ? createAvatarMascotSplit
      : createGlobalSplit
  const updateSavedSplit = isAvatarAdmin
    ? updateAvatarAdminSplit
    : isAvatarMascot
      ? updateAvatarMascotSplit
      : updateGlobalSplit
  const deleteSplit = isAvatarAdmin
    ? deleteAvatarAdminSplit
    : isAvatarMascot
      ? deleteAvatarMascotSplit
      : deleteGlobalSplit
  const { runWithStepUp } = useCampaignTotpStepUp()
  const [splits, setSplits] = useState<SplitFormItem[]>(() => [
    createEmptySplitFormItem(),
  ])
  const [lastFetchedIds, setLastFetchedIds] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    void runWithStepUp(() => fetchSplits())
      .then((data) => {
        if (cancelled) return
        const list = data.splits
        if (list.length === 0) {
          setSplits([createEmptySplitFormItem()])
          setLastFetchedIds([])
        } else {
          setSplits(list.map(mapApiSplitToFormItem))
          setLastFetchedIds(list.map((s) => s.id))
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err?.message ?? 'Erro ao carregar splits.')
          setSplits([createEmptySplitFormItem()])
          setLastFetchedIds([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [runWithStepUp, fetchSplits])

  const addSplit = useCallback(() => {
    setSplits((prev) => [...prev, createEmptySplitFormItem()])
  }, [])

  const removeSplit = useCallback((id: string) => {
    setSplits((prev) => {
      const next = prev.filter((s) => s.id !== id)
      return next.length > 0 ? next : [createEmptySplitFormItem()]
    })
  }, [])

  const updateSplit = useCallback(
    (id: string, patch: Partial<Omit<SplitFormItem, 'id'>>) => {
      setSplits((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)))
    },
    []
  )

  const handleSave = useCallback(async () => {
    setSaveMessage(null)
    const currentBackendIds = splits
      .filter((s) => isPersistedSplitId(s.id))
      .map((s) => Number(s.id))
    const toDelete = lastFetchedIds.filter((id) => !currentBackendIds.includes(id))
    const validationError = validateSplitFormItems(splits)
    if (validationError) {
      setSaveMessage(validationError)
      return
    }

    setIsSaving(true)
    try {
      for (const id of toDelete) {
        await runWithStepUp(() => deleteSplit(id))
      }
      for (const s of splits) {
        if (!isPersistedSplitId(s.id)) continue
        await runWithStepUp(() => updateSavedSplit(Number(s.id), toSplitUpdatePayload(s)))
      }
      for (const s of splits) {
        if (isPersistedSplitId(s.id)) continue
        const payload = toSplitCreatePayload(s)
        if (!payload) continue
        await runWithStepUp(() => createSplit(payload))
      }

      const fresh = await runWithStepUp(() => fetchSplits())
      const list = fresh.splits
      if (list.length === 0) {
        setSplits([createEmptySplitFormItem()])
        setLastFetchedIds([])
      } else {
        setSplits(list.map(mapApiSplitToFormItem))
        setLastFetchedIds(list.map((s) => s.id))
      }
      setSaveMessage('Splits salvos com sucesso.')
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Não foi possível salvar. Tente novamente.'
      setSaveMessage(msg)
    } finally {
      setIsSaving(false)
    }
  }, [
    runWithStepUp,
    splits,
    lastFetchedIds,
    fetchSplits,
    createSplit,
    updateSavedSplit,
    deleteSplit,
  ])

  if (loading) {
    return (
      <div className={styles.tabContent}>
        <div className={styles.splitsSection}>
          <p className={styles.saveMessage} role="status">
            Carregando splits...
          </p>
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className={styles.tabContent}>
        <div className={styles.splitsSection}>
          <p className={styles.saveMessage} role="alert">
            {loadError}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.tabContent}>
      <div className={styles.splitsSection}>
        <div className={styles.splitsHeader}>
          <h2 className={styles.splitsSectionTitle}>
            {isAvatarAdmin
              ? 'AVATAR ADM'
              : isAvatarMascot
                ? 'Split padrão avatar/mascote'
                : 'Splits padrão'}
          </h2>
          <button
            type="button"
            className={styles.addSplitBtn}
            onClick={addSplit}
            aria-label="Adicionar split"
          >
            <Icon icon="mdi:plus" width={20} height={20} />
            Adicionar split
          </button>
        </div>

        {isAvatarAdmin && (
          <p className={styles.saveMessage}>
            Configure os IDs Doey e os percentuais ou valores fixos para compras das peças
            do catálogo global do administrador. O ID vendedor será o da campanha onde o
            participante comprar a peça.
          </p>
        )}

        {isAvatarMascot && (
          <p className={styles.saveMessage}>
            Configure a distribuição das compras de itens de avatar e mascote. Cada compra
            usará o ID vendedor da campanha e os splits cadastrados nesta categoria.
          </p>
        )}

        <SplitFormRows
          splits={splits}
          idPrefix={
            isAvatarAdmin
              ? 'split-avatar-admin'
              : isAvatarMascot
                ? 'split-avatar-mascot'
                : 'split'
          }
          onChange={updateSplit}
          onRemove={removeSplit}
        />

        <div className={styles.splitsFooter}>
          {saveMessage && (
            <p className={styles.saveMessage} role="status">
              {saveMessage}
            </p>
          )}
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={isSaving}
            aria-busy={isSaving}
          >
            {isSaving ? (
              <>
                <Icon
                  icon="mdi:loading"
                  className={styles.spinnerIcon}
                  width={20}
                  height={20}
                />
                Salvando...
              </>
            ) : (
              <>
                <Icon icon="mdi:content-save-outline" width={20} height={20} />
                Salvar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
